import React, { useEffect } from 'react';
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useAutoScroll from 'hooks/useAutoScroll';
import useQueryParam, { useQueryParams } from 'hooks/useQueryParam';
import { useNavigate } from 'hooks/useNavigate';
import { useStatusRouteGuard } from 'hooks/useStatusRouteGuard';
import { setApplicationFlow, setApplicationStep } from 'handlers/applicationStep';
import { PreQualificationData, setPartnerName, setGoal, setPromoCode } from 'handlers/preQualificationData';
import { setCardData } from 'handlers/cardData';
import { setReferralApplicationId } from 'handlers/referrals';
import { getApplicationData, getCardDataForLoanApplication, getUserLocation, hstreamCallback } from 'thunks';
import { getState } from 'selectors/getState';
import { getApplicationData as applicationData } from 'selectors/getApplicationData';
import { getApplicationStep } from 'selectors/getApplicationStep';
import { getAuthData } from 'selectors/getAuthData';
import { setCredentials, setFullName, splitFullName } from 'handlers/yourName';
import { RoutePath } from 'enums/Routes';
import { CurrentFlow } from 'enums/CurrentFlow';
import { PartnerId } from 'enums/PartnerName';
import { LoanGoalName } from 'enums/LoanGoalName';
import { UtmCampaign, UtmSource } from 'enums/UtmTagName';
import { YourIncomeVariable } from 'enums/LoanFormVariables';
import { cleanNursegridFacilityParam } from 'utils/cleanNursegridFacilityParam';
import { getExistingPath } from 'utils/getExistingPath';
import { Environments, getEnvironment } from 'utils/getEnvironment';
import { COLOR_PARAM_TO_CARD_COLOR } from 'components/CardFlow/Customize/Customize';
import { CardColor } from 'components/CardFlow/Customize/cardVersions';
import { ProfessionGroup } from 'enums/ProfessionGroup';
import { setProfessionGroup } from 'handlers/professionGroup';
import { setNursegridUserId } from 'handlers/nursegridData';
import { setYourIncomeData, initialState as yourIncomeInitialState } from 'handlers/yourIncome';
import { UrlParams } from 'enums/UrlParams';
import { FLOW_ARG_TO_CURRENT_FLOW } from 'utils/flowArgToCurrentFlow';
import { initializeIntercom } from 'utils/intercom';
import Root from 'pages/Root';
import PageLoader from 'components/PageLoader';
import SmsLogin from 'components/SmsLogin';
import Error from 'components/Error';
import Logout from 'components/Logout';
import { getCredentialType, getValidatedCredentials } from 'utils/credentialUtils';

import FlowRouter from './FlowRouter';
import { CustomRouterType } from './types';

const FLOW_CHANGE_ROUTES = [RoutePath.XSellLoader];

export default () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useAutoScroll();
  useStatusRouteGuard();

  const state = useSelector(getState);
  const { stepName, currentFlow } = getApplicationStep(state);
  const { application, isLoading: isLoadingApplication } = applicationData(state);
  const { hstreamUserInfo } = useSelector(getAuthData);
  const params = useQueryParams();
  const isResumeProcess = params.has(UrlParams.ResumeProcess);
  const flowParam = useQueryParam(UrlParams.Flow);
  const flow = !flowParam || flowParam === 'checkup' ? 'ai-coach' : flowParam;
  const applicationId = useQueryParam(UrlParams.ResumeProcess);

  // HStream OAuth callback detection
  const hstreamCode = useQueryParam(UrlParams.HStreamCode);
  const hstreamState = useQueryParam(UrlParams.HStreamState);
  const isHStreamCallback = hstreamCode && hstreamState === 'plannery-hstream';
  const [isHStreamLoading, setIsHStreamLoading] = React.useState(isHStreamCallback && !hstreamUserInfo);

  const utmSource = useQueryParam(UrlParams.UtmSource);
  const utmContent = useQueryParam(UrlParams.UtmContent);
  const utmCampaign = useQueryParam(UrlParams.UtmCampaign);
  const initialReferrer = useQueryParam(UrlParams.InitialReferrer);

  const fullName = useQueryParam(UrlParams.FullName);
  const credentials = useQueryParam(UrlParams.Credentials);
  const cardColor: CardColor | undefined = COLOR_PARAM_TO_CARD_COLOR[useQueryParam(UrlParams.CardColor) || ''];
  const professionGroup = params.get(UrlParams.ProfessionGroup);
  const partnerId = useQueryParam(UrlParams.Partner);
  const benefit = useQueryParam(UrlParams.Benefit) === 'true';
  const nursegridUserId = useQueryParam(UrlParams.UserId);
  const employerName = useQueryParam(UrlParams.EmployerName);
  const nursegridCredential = useQueryParam(UrlParams.NursegridCredential);
  const promoCode = useQueryParam(UrlParams.PromoCode);

  const handleInitialQueryParams = () => {
    let partnerParam = searchParams.get(UrlParams.Partner);
    const benefitParam = searchParams.get(UrlParams.Benefit) === 'true';
    if (partnerParam) {
      partnerParam = partnerParam.toLowerCase();
      const existingPartner =
        getEnvironment() === Environments.Staging
          ? (partnerParam as PartnerId)
          : Object.values(PartnerId).find((partner) => partner === partnerParam);
      if (existingPartner) {
        const preQualificationData: PreQualificationData = {
          partnerId: existingPartner!,
          benefit: benefitParam,
        };
        dispatch(setPartnerName(preQualificationData));
      }
    }

    const goalParam = searchParams.get(UrlParams.Goal);
    if (goalParam) {
      const existingGoal = Object.entries(LoanGoalName).find(([key]) => key === goalParam)?.[1];
      const goal = existingGoal || goalParam;
      dispatch(setGoal(goal));
    }

    if (utmSource === UtmSource.Referral) {
      const referrer = utmContent || '';
      const isCardReferral = utmCampaign === UtmCampaign.PlanneryCard;
      if (isCardReferral) {
        dispatch(
          setCardData({
            referredBy: referrer,
            initialReferrer: initialReferrer || '',
          }),
        );
      } else {
        dispatch(setReferralApplicationId(referrer));
      }
    }

    if (fullName) {
      dispatch(setFullName({ fullName }));
    }

    if (credentials) {
      dispatch(setCardData({ borrowerCredentials: credentials }));
    }

    if (cardColor) {
      dispatch(setCardData({ cardColor }));
    }

    if (professionGroup) {
      dispatch(setProfessionGroup(professionGroup as ProfessionGroup));
    }

    if (nursegridUserId) {
      dispatch(setNursegridUserId(nursegridUserId));
    }

    if (employerName) {
      const employerNameCleaned = cleanNursegridFacilityParam(employerName);

      dispatch(
        setYourIncomeData({
          ...yourIncomeInitialState,
          [YourIncomeVariable.EmployerName]: employerNameCleaned,
        }),
      );
    }

    if (nursegridCredential) {
      const validatedCredentials = getValidatedCredentials(nursegridCredential);
      const validCredentials = validatedCredentials.join(', ');
      const credentialsToStore = validatedCredentials.length > 0 ? validCredentials : undefined;
      const credentialType = getCredentialType(false, true, credentialsToStore);

      if (credentialsToStore) {
        dispatch(
          setCredentials({
            credentials: credentialsToStore,
            type: credentialType,
          }),
        );
      }
    }

    if (promoCode) {
      dispatch(setPromoCode(promoCode));
    }
  };

  useEffect(() => {
    const { firstName, lastName } = splitFullName(fullName);

    analytics.identify({
      ...(professionGroup && { professionGroup }),
      ...(cardColor && { cardColor }),
      ...(fullName && { name: fullName }),
      ...(firstName && { firstName }),
      ...(lastName && { lastName }),
      ...(credentials && { credentials }),
      ...(partnerId && { partner: partnerId }),
      ...(benefit && { benefit }),
    });

    // HStream OAuth callback handling
    if (isHStreamCallback && !hstreamUserInfo && isHStreamLoading) {
      dispatchWithUnwrap(hstreamCallback({ code: hstreamCode! }))
        .then((response) => {
          const { userInfo } = response;

          analytics.identify({
            email: userInfo.email,
            firstName: userInfo.givenName,
            lastName: userInfo.familyName,
            hstreamId: userInfo.hstreamId,
            hstreamUserId: userInfo.userId,
            partner: PartnerId.HStream,
          });

          setIsHStreamLoading(false);

          // Clear URL parameters and navigate to start of flow
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete(UrlParams.HStreamCode);
          newUrl.searchParams.delete(UrlParams.HStreamState);
          window.history.replaceState({}, '', newUrl.toString());

          // Set the flow if not already set
          if (!stepName) {
            const selectedFlow = FLOW_ARG_TO_CURRENT_FLOW[flow!];
            dispatch(
              setApplicationFlow({
                currentFlow: selectedFlow ?? CurrentFlow.AICoach,
                stepName: undefined,
              }),
            );
          }
        })
        .catch(() => {
          setIsHStreamLoading(false);
          navigate(RoutePath.Error, { replace: true });
        });
    }

    if (stepName === undefined && getExistingPath(location.pathname) && currentFlow === undefined) {
      // Backwards compatibility for old applications
      dispatch(setApplicationFlow({ currentFlow: CurrentFlow.AICoach, stepName: getExistingPath(location.pathname) }));
    }

    // If there is a resume process
    if (isResumeProcess && !application && !isLoadingApplication) {
      dispatchWithUnwrap(getApplicationData(applicationId!)).then((response) => {
        if (response.application?.cardApplied) {
          dispatchWithUnwrap(getCardDataForLoanApplication(response.application.id));
        }
        dispatch(
          setCardData({
            applied: response.application.cardApplied,
          }),
        );
        analytics.identify(response.application.borrowerId);
        (window as any).nid('setUserId', response.application.id);
      });
    }

    // if there is no step data and resume process, set flow and stepname
    if (!isResumeProcess && !isHStreamCallback && !stepName && !getExistingPath(location.pathname)) {
      const selectedFlow = FLOW_ARG_TO_CURRENT_FLOW[flow!];
      dispatch(
        setApplicationFlow({
          currentFlow: selectedFlow ?? CurrentFlow.AICoach,
          stepName: !selectedFlow ? RoutePath.Error : undefined,
          completedCustomize: Boolean(
            firstName &&
              lastName &&
              !lastName.includes(' ') &&
              cardColor &&
              (credentials || professionGroup === ProfessionGroup.Healthcare) &&
              professionGroup,
          ),
        }),
      );
    }

    const trackUserLocation = async () => {
      const userLocation = await dispatchWithUnwrap(getUserLocation());
      if (userLocation?.clientCountryRegion) {
        analytics.identify({
          clientCountry: userLocation.clientCountryRegion.substr(0, 2),
          clientRegion: userLocation.clientCountryRegion.substr(2),
          clientCity: userLocation.clientCity,
        });
      }
    };

    handleInitialQueryParams();
    trackUserLocation();
  }, []);

  useEffect(() => {
    const pathName = getExistingPath(location.pathname);
    // Navigate to stepName if the current location does not exist or the current route expects a flow change
    if (stepName && (!pathName || FLOW_CHANGE_ROUTES.includes(pathName)) && stepName !== location.pathname) {
      navigate(stepName, { replace: !pathName });
    }
  }, [stepName]);

  useEffect(() => {
    if (location) {
      const actualPath = getExistingPath(location.pathname);
      if (actualPath) {
        dispatch(setApplicationStep(actualPath));
      }
    }
  }, [location]);

  useEffect(() => {
    initializeIntercom();
  }, []);

  if (isResumeProcess && !application) {
    return (
      <Routes>
        <Route path="*" element={<PageLoader />} />
      </Routes>
    );
  }

  // Show PageLoader while processing HStream callback
  if (isHStreamLoading) {
    return (
      <Routes>
        <Route path="*" element={<PageLoader />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Root />}>
        <Route path={RoutePath.SmsLogin} element={<SmsLogin />} />
        {currentFlow !== undefined &&
          Object.entries({
            ...FlowRouter[currentFlow],
            ...{
              [RoutePath.Error]: {
                component: Error,
              },
              [RoutePath.Logout]: {
                component: Logout,
              },
            },
          }).map(([path, router]) => {
            const { component: Component, navigationInfo, handleNext } = router as CustomRouterType;
            return (
              <Route
                path={path}
                element={
                  <Component
                    navigationInfo={navigationInfo || {}}
                    handleNext={handleNext ? handleNext({ state, navigate, dispatch, dispatchWithUnwrap }) : () => {}}
                  />
                }
                key={path}
              />
            );
          })}
      </Route>
    </Routes>
  );
};
